<template>
    <div>
        <div
            class="position-relative"
            :class="disabled && 'cursor--not-allowed opacity--50'"
        >
            <date-picker
                :key="key"
                :model-value="value"
                :max-date="maxDate"
                :min-date="minDate"
                :class="disabled && 'pointer-events-none'"
                :update-on-input="true"
                :locale="locale"
                :model-modifiers="{ range: mode === 'range' }"
                :popover="{ visibility: 'click' }"
                mode="date"
                color="primary"
                @update:model-value="input"
            >
                <template #default="{ togglePopover }">
                    <div class="position-relative">
                        <input
                            :value="inputValue"
                            class="border pl-44 pr-44 rounded-sm w-100"
                            style="height: 52px"
                            :class="inputClasses"
                            :disabled="disabled"
                            readonly
                            @click="!disabled && togglePopover()"
                        />

                        <v-icon
                            name="calendar-alt"
                            class="left ml-12 text-gray-dark abs-center-y position-absolute"
                            :w="20"
                        />

                        <button-link
                            v-if="showErase"
                            icon="close"
                            :icon-size="20"
                            class="right mr-12 abs-center-y position-absolute"
                            :disabled="disabled"
                            theme="primary"
                            @click.prevent="erase"
                        />

                        <div
                            v-if="innerLabel"
                            class="position-absolute top left text-gray-dark pointer-events-none text-truncate overflow-hidden w-100 font--12 lh--16 pt-8 pl-44"
                            :class="labelClass"
                        >
                            {{ innerLabel }}
                        </div>
                    </div>
                </template>
            </date-picker>
        </div>

        <input-status :status="status" />
    </div>
</template>

<script>
import { DatePicker } from "v-calendar"
import "v-calendar/style.css"
import ButtonLink from "~/components/buttons/ButtonLink"
import InputStatus from "~/components/inputs/InputStatus"

export default {
    props: {
        ...InputStatus.props,
        value: [Date, Object, Array],
        disabled: Boolean,
        innerLabel: String,
        maxDate: {
            type: Date,
            default: undefined,
        },

        minDate: {
            type: Date,
            default: undefined,
        },

        mode: {
            type: String,
            default: "single",
            validator: val => ["single", "range"].includes(val),
        },

        needsBottomMargin: {
            // Could have a better name
            type: Boolean,
            default: false,
        },
    },

    emits: ["update:value"],

    data() {
        return {
            key: 0,
        }
    },

    computed: {
        inputValue() {
            if (!this.value) return ""

            switch (this.mode) {
                case "single":
                    return this.formatDate(this.value)

                case "range": {
                    const { start, end } = this.value
                    return [start, end].map(this.formatDate).join(" - ")
                }

                default:
                    throw new Error(`Unexpected mode ${this.mode}`)
            }
        },

        inputClasses() {
            const status = this.status.code
            return {
                border: true,
                "py-15": !this.innerLabel,
                "pt-22 pb-8": this.innerLabel,
                "pl-44": true,
                "pr-44": this.showErase,
                "rounded-sm": true,
                "w-100": true,
                "placeholder-gray-dark": true,
                "border-danger": status == "danger",
                "border-info": status == "info",
                "border-success": status == "success",
                "border-warning": status == "warning",
                "border-gray-medium": this.disabled,
            }
        },

        labelClass() {
            return {
                "pr-44": this.showErase,
                "pr-12": !this.showErase,
            }
        },

        showErase() {
            if (!this.value || this.disabled) return false
            switch (this.mode) {
                case "single":
                    return true

                case "range":
                    return this.value.start && this.value.end

                default:
                    throw new Error(`Unexpected mode ${this.mode}`)
            }
        },

        locale() {
            return this.$store.getters.locale
        },

        timeZone() {
            return this.$store.getters.timeZone
        },

        primaryColor() {
            return this.$store.getters["theme/primaryColor"]
        },
    },

    watch: {
        value: {
            handler() {
                this.key += 1
            },
        },
    },

    methods: {
        input(v) {
            if (this.disabled) return
            this.$emit("update:value", v)
        },

        erase() {
            if (this.disabled) return
            this.$emit("update:value", null)
        },

        formatDate(date) {
            return date.toLocaleString(this.locale, {
                timeZone: this.timeZone,
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
            })
        },
    },

    components: {
        ButtonLink,
        InputStatus,
        DatePicker,
    },
}
</script>
